import React, { useState } from 'react';
import DashboardLayout from '../components/layouts/DashboardLayout';
import ReadingAndWritingGrid from '../components/skill_practice/ReadingAndWritingGrid';
import ListeningGrid from '../components/skill_practice/ListeningGrid';
import SpeakingGrid from '../components/skill_practice/SpeakingGrid';

const SKILL_TABS = [
  {
    key: 'listening',
    label: 'Listening',
    labelVi: 'Nghe',
    emoji: '🎧',
    color: '#6366f1',
    colorLight: '#eef2ff',
    grad: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    glow: 'rgba(99,102,241,0.35)',
  },
  {
    key: 'reading_writing',
    label: 'Reading & Writing',
    labelVi: 'Đọc & Viết',
    emoji: '📖',
    color: '#f59e0b',
    colorLight: '#fffbeb',
    grad: 'linear-gradient(135deg, #f59e0b, #f97316)',
    glow: 'rgba(245,158,11,0.35)',
  },
  {
    key: 'speaking',
    label: 'Speaking',
    labelVi: 'Nói',
    emoji: '🎤',
    color: '#10b981',
    colorLight: '#ecfdf5',
    grad: 'linear-gradient(135deg, #10b981, #06b6d4)',
    glow: 'rgba(16,185,129,0.35)',
  },
];

// ─── inject animations once ───────────────────────────────────────────────────
let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected || document.getElementById('skill-practice-styles')) return;
  stylesInjected = true;
  const s = document.createElement('style');
  s.id = 'skill-practice-styles';
  s.textContent = `
    @keyframes spFloat {
      0%,100% { transform: translateY(0) rotate(-6deg); }
      50%      { transform: translateY(-16px) rotate(6deg); }
    }
    @keyframes spSlideIn {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .sp-float { animation: spFloat 3.5s ease-in-out infinite; }
    .sp-float-b { animation: spFloat 4.5s ease-in-out infinite; animation-delay: 1s; }
    .sp-slide-in { animation: spSlideIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both; }
    .sp-tab:hover { transform: translateY(-2px); }
    .sp-tab { transition: all 0.25s cubic-bezier(0.34,1.56,0.64,1); }
  `;
  document.head.appendChild(s);
};

const SkillPractice = () => {
  injectStyles();
  const [activeTab, setActiveTab] = useState('listening');

  const activeConfig = SKILL_TABS.find(t => t.key === activeTab);

  const renderGrid = () => {
    switch (activeTab) {
      case 'reading_writing': return <ReadingAndWritingGrid />;
      case 'listening': return <ListeningGrid />;
      case 'speaking': return <SpeakingGrid />;
      default: return null;
    }
  };

  return (
    <DashboardLayout>
      <div
        className="flex-1 overflow-y-auto p-4 md:p-8"
        style={{ background: 'linear-gradient(160deg, #f5f3ff 0%, #eef2ff 40%, #f0fdf4 100%)' }}
      >
        <div className="max-w-6xl mx-auto">

          {/* ── Page Header ─────────────────────────────────────────────── */}
          <div style={{ textAlign: 'center', marginBottom: 32, position: 'relative' }}>
            {/* Floating decorations */}
            <span className="sp-float" style={{ position: 'absolute', left: '10%', top: 0, fontSize: 28, opacity: 0.7 }}>⭐</span>
            <span className="sp-float-b" style={{ position: 'absolute', right: '12%', top: 8, fontSize: 24, opacity: 0.6 }}>✨</span>

            <div style={{ fontSize: 22, marginBottom: 4, letterSpacing: 8 }}>🌟 ✨ 🌟</div>
            <h1 style={{
              fontSize: 'clamp(26px,4vw,40px)', fontWeight: 900, margin: '0 0 8px',
              background: 'linear-gradient(135deg, #6366f1, #f59e0b, #10b981)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              ✏️ Luyện Kỹ Năng
            </h1>
            <p style={{ color: '#64748b', fontSize: 15, margin: 0 }}>
              Chọn kỹ năng và bắt đầu luyện tập từng phần nhé! 💪
            </p>
          </div>

          {/* ── Skill Tab Cards ──────────────────────────────────────────── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
            marginBottom: 32,
          }}>
            {SKILL_TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  className="sp-tab"
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    background: isActive ? tab.grad : 'white',
                    border: isActive ? '3px solid transparent' : '2px solid rgba(0,0,0,0.06)',
                    borderRadius: 24,
                    padding: '20px 12px',
                    cursor: 'pointer',
                    boxShadow: isActive
                      ? `0 8px 28px ${tab.glow}`
                      : '0 2px 12px rgba(0,0,0,0.06)',
                    transform: isActive ? 'translateY(-3px)' : 'translateY(0)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 10,
                    outline: 'none',
                  }}
                >
                  <span style={{
                    fontSize: 36, lineHeight: 1,
                    filter: isActive ? 'drop-shadow(0 3px 8px rgba(0,0,0,0.25))' : 'none',
                  }}>
                    {tab.emoji}
                  </span>
                  <div style={{
                    fontWeight: 800, fontSize: 14,
                    color: isActive ? 'white' : '#475569',
                    lineHeight: 1.3, textAlign: 'center',
                  }}>
                    {tab.labelVi}
                    <div style={{ fontSize: 11, fontWeight: 500, opacity: isActive ? 0.85 : 0.6, marginTop: 2 }}>
                      {tab.label}
                    </div>
                  </div>
                  {isActive && (
                    <div style={{
                      width: 28, height: 4, borderRadius: 2,
                      background: 'rgba(255,255,255,0.6)',
                    }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Active skill section header ─────────────────────────────── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            marginBottom: 20, padding: '16px 20px',
            background: activeConfig.colorLight,
            borderRadius: 18,
            border: `2px solid ${activeConfig.color}20`,
          }}
            className="sp-slide-in"
            key={activeTab}
          >
            <span style={{ fontSize: 32 }}>{activeConfig.emoji}</span>
            <div>
              <div style={{ fontWeight: 900, fontSize: 18, color: '#1e293b' }}>
                {activeConfig.labelVi}
                <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 600, color: activeConfig.color }}>
                  ({activeConfig.label})
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                Chọn phần bạn muốn luyện tập 👇
              </div>
            </div>
          </div>

          {/* ── Part list ───────────────────────────────────────────────── */}
          <div key={activeTab + '-grid'} className="sp-slide-in">
            {renderGrid()}
          </div>

          {/* ── Footer ──────────────────────────────────────────────────── */}
          <div style={{ textAlign: 'center', marginTop: 40, opacity: 0.7 }}>
            <div style={{ fontSize: 26, marginBottom: 6, letterSpacing: 6 }}>🦋 🌈 🚀</div>
            <p style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>
              Mỗi ngày luyện tập một chút, bạn sẽ tiến bộ rất nhanh! 💪
            </p>
          </div>

        </div>
      </div>
    </DashboardLayout>
  );
};

export default SkillPractice;
